// Client helpers for viewing RET .txtrpt scans.
//
// A .txtrpt is the system source of truth but reads poorly in a browser (it
// downloads or opens blank). Its slots therefore offer three actions — View
// Report (PDF) / Copy TXTRPT / Download Original — shared by the crew wizard
// (SMRTCOP.tsx) and the admin review views (SMRTCOP.tsx + ManageSMRTCOP.tsx).

// Minimal shape the helpers need; each page has its own fuller ReportFileRef.
export interface TxtrptFileRef {
  name: string;
  blobName: string;
  // Sibling PDF stored on upload; absent for scans predating PDF storage.
  pdfBlobName?: string;
}

// RET scan slots are keyed ret_* (ret_alpha_p1 …, plus the legacy ret_report).
export function isTxtrptSlot(fieldKey: string): boolean {
  return /^ret_/.test(fieldKey);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// Fetch a stored blob's bytes via the base64 proxy (direct SAS is firewalled).
async function loadSmrtcopBase64(
  siteId: string,
  fieldKey: string,
  blobName: string,
): Promise<{ base64: string; contentType: string }> {
  const r = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(siteId)}/${encodeURIComponent(fieldKey)}?blobName=${encodeURIComponent(blobName)}&format=base64`,
    { credentials: "include" },
  );
  if (!r.ok) throw new Error(`load ${r.status}`);
  return r.json();
}

// Open the readable PDF for a RET scan in a new tab. Prefers the PDF stored on
// upload; falls back to on-demand generation for legacy scans. Pass a window
// opened synchronously in the click handler so popup blockers don't swallow it.
export async function openTxtrptReportPdf(
  win: Window | null,
  siteId: string,
  fieldKey: string,
  ref: TxtrptFileRef,
): Promise<void> {
  const endpoint = ref.pdfBlobName
    ? `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(siteId)}/${encodeURIComponent(fieldKey)}?blobName=${encodeURIComponent(ref.pdfBlobName)}&format=base64`
    : `${process.env.REACT_APP_BACKEND_URL}/smrtcop/txtrpt-pdf/${encodeURIComponent(siteId)}/${encodeURIComponent(fieldKey)}?blobName=${encodeURIComponent(ref.blobName)}`;
  const r = await fetch(endpoint, { credentials: "include" });
  if (!r.ok) throw new Error(`pdf ${r.status}`);
  const data = await r.json();
  const url = URL.createObjectURL(
    new Blob([base64ToBytes(data.base64)], { type: "application/pdf" }),
  );
  if (win) win.location.href = url;
  else window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Copy the raw .txtrpt text to the clipboard. The .txtrpt is preserved intact
// (never modified) so the app keeps using it as the source for extraction.
export async function copyTxtrptText(
  siteId: string,
  fieldKey: string,
  ref: TxtrptFileRef,
): Promise<void> {
  const data = await loadSmrtcopBase64(siteId, fieldKey, ref.blobName);
  const text = new TextDecoder().decode(base64ToBytes(data.base64));
  await navigator.clipboard.writeText(text);
}
